<script setup lang="tsx">
import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import CenteredContent from '@/components/content/CenteredContent.vue';
import FormHeader from '@/components/formHeader/FormHeader.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';

import BrowsingOnlineIllustration from '@/images/browsing-online-illustration.svg';
import SpreadsheetIllustration from '@/images/spreadsheet-illustration.svg';

const props = defineProps<{
  uploadType: App.Bulkinator.Data.UploadTypeData;
  testTypes: App.Base.Data.EnumData[];
}>();

const isStudentUploadType = computed(() => props.uploadType.id === 'student');

const selectedTestType = ref<App.Base.Data.EnumData | null>(null);
const customTestUploadType = computed(() => {
  if (selectedTestType.value) {
    return `test-${selectedTestType.value?.value}`;
  }

  return null;
});

function handleClose() {
  router.get(
    route('upload-sessions.draft.index', {
      uploadType: props.uploadType.id
    })
  );
}
</script>


					<script lang="tsx">
					import __hybridly_layout_0 from '~/resources/layouts/default.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <CenteredContent class="px-4">
    <FormHeader
      title="Create a New Session"
      description="Select the creation method of your new session."
      canClose
      @onClose="handleClose"
    />

    <div v-if="uploadType.id === 'test'" class="space-y-12 py-6">
      <FormListboxInput v-model="selectedTestType" :options="testTypes" label="Select test type" />
      <div v-if="!selectedTestType">
        <p class="text-center text-lg font-semibold text-zinc-700">
          You must select a test type to continue with this upload session.
        </p>
      </div>
    </div>

    <div v-if="uploadType.id !== 'test' || selectedTestType" class="py-8">
      <p class="mb-8 text-lg font-semibold leading-6 text-zinc-900">
        Where are you editing your data?
      </p>
      <div
        class="flex origin-top scale-110 flex-col items-center gap-4 lg:flex-row"
        :class="{
          'flex-col-reverse lg:flex-row-reverse': isStudentUploadType
        }"
      >
        <div
          class="space-y-6 rounded-lg px-6 py-12 text-center"
          :class="{
            'origin-right border border-zinc-200 lg:scale-95': isStudentUploadType,
            'border border-zinc-500 ring-3 ring-zinc-300/50': !isStudentUploadType
          }"
        >
          <h3 class="text-xl font-bold">
            I want to edit my data<br />
            in GRACE
          </h3>

          <BrowsingOnlineIllustration class="mx-auto w-36" />

          <p class="text-sm font-medium text-zinc-500">
            Use the Bulkinator UI to make changes to existing GRACE data in bulk.
          </p>

          <Button
            :size="ButtonSize.lg"
            :variant="isStudentUploadType ? ButtonVariant.soft : ButtonVariant.solid"
            :color="isStudentUploadType ? ButtonColor.slate : ButtonColor.primary"
            :href="
              route('upload-sessions.create.existing', {
                uploadType: customTestUploadType ?? uploadType.id
              })
            "
            useMediaQuery
            >Use GRACE to edit my data</Button
          >
        </div>

        <div
          class="space-y-6 rounded-lg px-6 py-12 text-center"
          :class="{
            'origin-left border border-zinc-500 ring-3 ring-zinc-300/50 lg:scale-105':
              isStudentUploadType,
            'border border-zinc-200': !isStudentUploadType
          }"
        >
          <h3 class="text-lg font-bold">
            I want to edit my data
            <br />in Excel
          </h3>

          <SpreadsheetIllustration class="mx-auto w-36" />

          <p class="text-sm font-medium text-zinc-500">
            Use Excel to make edits to your GRACE data then upload that file here.
          </p>

          <Button
            :size="ButtonSize.lg"
            :variant="isStudentUploadType ? ButtonVariant.solid : ButtonVariant.soft"
            :color="isStudentUploadType ? ButtonColor.primary : ButtonColor.slate"
            :href="
              route('upload-sessions.create.manual', {
                uploadType: customTestUploadType ?? uploadType.id
              })
            "
            useMediaQuery
            >Upload a file</Button
          >
        </div>
      </div>
    </div>
  </CenteredContent>
</template>
